// src/oura/merchant/drawers/PlanDrawer.tsx
//
// "Your plan": every date and every piece of work in one place, month by
// month (redesign, 21 Oct 2026). It replaces two lists a merchant could not
// tell apart -- "Your dates" and "All 12 pieces of work" -- because both read
// from the same execution timeline.
//
// Each month of the published schedule is one fold; only the month we are in
// opens by itself. Inside, a spine of stops: this month's work first, then
// each dated stop. A dot says where the plan is: a blue tick for done, a
// yellow ring for the stop we are at, a grey ring for one not reached yet.
//
// NOTHING HERE IS COMPUTED FROM TODAY'S DATE. A month is behind, current or
// ahead by the server's `currentMonth`; a date is still ahead because the
// server lists it. A date lands in the first month whose report is on or
// after it. A line sits in the month the server says (`month`, sent only on
// an earlier month's line still open), else in this month. What later months
// hold is the engine's own words (`lands`); their lines arrive when they start.

import { useState, type ReactNode } from "react";
import { CheckIcon, ChevronDownIcon, PenLineIcon } from "lucide-react";
import type { ClientPagePayload, ClientPlanItem } from "@service/aeo/clientReview";
import { ArrowLink, Drawer, DrawerBlock, FOCUS_RING, LATE_TEXT, PillButton, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { OURA_ROOT } from "../../nav";
import { asksOf } from "../asks";
import { DATE_NOTE, lineDescription, lineWhen, measuredItems, openAskFor, planLineTitle, REPORT_ROW, slipNote, sortPlanItems, type ComingDate } from "../model";
import { measuredSentence } from "./WorkDrawer";
import { REPORTS_PAGE } from "../../merchantV2";

type Dot = "done" | "now" | "ahead";

/** The three marks, at 18px on the spine and 14px on a line. */
export function PlanDot({ state, size = 18 }: { state: Dot; size?: number }) {
    if (state === "done") {
        return (
            <span aria-hidden className="grid shrink-0 place-items-center rounded-full bg-blue-600 text-white" style={{ width: size, height: size }}>
                <CheckIcon size={Math.round(size * 0.62)} strokeWidth={3.5} />
            </span>
        );
    }
    return (
        <span
            aria-hidden
            className={cn("block shrink-0 rounded-full border-2 bg-white", state === "now" ? "border-amber-400" : "border-gray-300")}
            style={{ width: size, height: size }}
        />
    );
}

const DOT_WORD: Record<Dot, string> = { done: "Done", now: "In progress", ahead: "Not reached yet" };

/** A line's own mark: done, moving (ours, Google's or theirs), or not started. */
function lineDot(item: ClientPlanItem): Dot {
    if (item.status === "done") return "done";
    if (item.status === "not_started" || item.status === "reported_by_hand") return "ahead";
    return "now";
}

interface Stop {
    key: string;
    date: string | null;
    label: string;
    note: string | null;
    slip: ReturnType<typeof slipNote>;
    lines: ClientPlanItem[];
    /** Later months: what lands, in the engine's words. */
    lands: string[];
}

export function PlanDrawer({
    open,
    onClose,
    payload,
    dates,
    openKey,
    onAnswer,
}: {
    open: boolean;
    onClose: () => void;
    payload: ClientPagePayload;
    dates: ComingDate[];
    /** The line to open when the drawer opens (a row on Home), by key or code. */
    openKey: string | null;
    onAnswer: (askId: string) => void;
}) {
    const { schedule, engagement } = payload;
    const items = payload.plan && payload.subscription ? sortPlanItems(payload.plan.items) : [];
    const asks = asksOf(payload.packs);
    const cur = schedule?.currentMonth ?? payload.plan?.month ?? 1;
    const months = schedule?.months ?? [];
    const keyOf = (i: ClientPlanItem) => i.key ?? i.code;
    const monthOfLine = (i: ClientPlanItem) => i.month ?? cur;
    const monthOfDate = (date: string) => months.find((m) => date <= m.reportDate)?.termMonth ?? months[months.length - 1]?.termMonth ?? cur;

    const openLine = openKey ? items.find((i) => keyOf(i) === openKey) ?? null : null;
    const [openMonths, setOpenMonths] = useState<ReadonlySet<number>>(() => new Set([openLine ? monthOfLine(openLine) : cur]));
    const [openLines, setOpenLines] = useState<ReadonlySet<string>>(() => new Set(openKey ? [openKey] : []));
    const flip = <T,>(set: ReadonlySet<T>, v: T) => {
        const next = new Set(set);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        return next;
    };

    const stopsOf = (termMonth: number, reportDate: string, lands: string[]): Stop[] => {
        const lines = items.filter((i) => monthOfLine(i) === termMonth);
        // A line waiting on an ask hangs under the date that waits on the same ask.
        const askOf = (i: ClientPlanItem) => openAskFor(i, asks);
        const dated = dates.filter((d) => monthOfDate(d.date) === termMonth);
        const placed = new Set<string>();
        const dateStops: Stop[] = dated.map((d) => {
            const under = d.itemId ? lines.filter((i) => askOf(i) === d.itemId) : [];
            under.forEach((i) => placed.add(keyOf(i)));
            return { key: d.key, date: d.date, label: d.label, note: DATE_NOTE[d.kind], slip: slipNote(d), lines: under, lands: [] };
        });
        // A report the server did not list (an earlier month, or no upcoming list) still closes its month.
        if (!dated.some((d) => d.kind === "report")) {
            dateStops.push({ key: `report-${termMonth}`, date: reportDate, label: REPORT_ROW, note: DATE_NOTE.report, slip: null, lines: [], lands: [] });
        }
        const rest = lines.filter((i) => !placed.has(keyOf(i)));
        const work: Stop[] = rest.length > 0 || (termMonth > cur && lands.length > 0)
            ? [{ key: `work-${termMonth}`, date: null, label: termMonth === cur ? "This month's work" : `Month ${termMonth} work`, note: null, slip: null, lines: rest, lands: termMonth > cur ? lands : [] }]
            : [];
        return [...work, ...dateStops];
    };

    // Behind is done, ahead is not reached; in this month the first stop not done is where we are.
    const statesOf = (termMonth: number, stops: Stop[]): Dot[] => {
        if (termMonth > cur) return stops.map(() => "ahead");
        if (termMonth < cur) return stops.map((s) => (s.lines.every((i) => i.status === "done") ? "done" : "now"));
        let found = false;
        return stops.map((s) => {
            // A dated stop in this month is still ahead (the server lists it), so only the work can be done.
            const done = s.date === null && s.lines.length > 0 && s.lines.every((i) => i.status === "done");
            if (done) return "done";
            if (found) return "ahead";
            found = true;
            return "now";
        });
    };

    // The execution timeline is an engagement document in the merchant's Reports, named by staff. Found by
    // its title, it opens there in its own drawer (`?report=`); not found, the link opens Reports alone.
    const timeline = payload.reports.find((r) => /timeline/i.test(r.title)) ?? null;
    const timelineHref = timeline ? `${OURA_ROOT}/reports?report=${encodeURIComponent(timeline.id)}` : `${OURA_ROOT}/reports`;

    const measured = measuredItems(items);
    const doneCount = measured.filter((i) => i.status === "done").length;
    const left = schedule ? Math.max(0, months.length - cur) : null;

    return (
        <Drawer open={open} onClose={onClose} title="Your plan" sub="Every date and every piece of work, month by month" width={760} closeTapTarget>
            <DrawerBlock>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <span className="font-semibold text-gray-900">
                        {measured.length > 0 ? `${doneCount} of ${measured.length} pieces done` : "No work scheduled yet"}
                    </span>
                    {schedule && (
                        <span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>
                            Month {cur} of {months.length}
                            {left !== null && ` · ${left} ${left === 1 ? "month" : "months"} left`}
                        </span>
                    )}
                </div>
                {months.length > 0 && (
                    <div aria-hidden className="mt-2.5 flex gap-[3px]">
                        {months.map((m) => (
                            <span key={m.termMonth} className={cn("h-1.5 min-w-0 flex-1 rounded-full", m.termMonth < cur ? "bg-blue-600" : m.termMonth === cur ? "bg-amber-400" : "bg-gray-200")} />
                        ))}
                    </div>
                )}
                {schedule && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2.5">
                        <span className={cn(TYPE.tc, "min-w-0 text-gray-600")}>
                            <span className="font-medium text-gray-900">Execution timeline</span>
                            {dayDate(schedule.publishedAt) ? ` · updated ${dayDate(schedule.publishedAt)}` : ""}
                        </span>
                        <ArrowLink to={timelineHref}>Open in {REPORTS_PAGE}</ArrowLink>
                    </div>
                )}
                <div className={cn(TYPE.tc, "mt-3 flex flex-wrap gap-x-4 gap-y-2 border-t border-gray-100 pt-3 text-gray-600")}>
                    {(["done", "now", "ahead"] as const).map((s) => (
                        <span key={s} className="inline-flex items-center gap-1.5">
                            <PlanDot state={s} size={14} />
                            {DOT_WORD[s]}
                        </span>
                    ))}
                </div>
            </DrawerBlock>

            {schedule?.changed && (
                <div className={cn(TYPE.tc, "flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-amber-900")}>
                    <PenLineIcon aria-hidden size={14} className="mt-0.5 shrink-0" />
                    <span className="min-w-0">
                        <span className="font-medium">Dates moved.</span> {schedule.changed.summary}
                    </span>
                </div>
            )}

            {/* A plan can start before its dates are published: the work is already on Home, so it is here too, undated. */}
            {months.length === 0 && (
                <>
                    <DrawerBlock>Your dates are not published yet. Your account manager publishes the whole term, and it appears here.</DrawerBlock>
                    {items.length > 0 && (
                        <DrawerBlock label="Your work">
                            <ul className="list-plain m-0 flex flex-col gap-2 p-0">
                                {items.map((item) => (
                                    <PlanLineRow
                                        key={keyOf(item)}
                                        item={item}
                                        engagement={engagement}
                                        askId={item.status === "waiting_on_you" ? openAskFor(item, asks) : null}
                                        open={openLines.has(keyOf(item))}
                                        onToggle={() => setOpenLines((set) => flip(set, keyOf(item)))}
                                        onAnswer={onAnswer}
                                    />
                                ))}
                            </ul>
                        </DrawerBlock>
                    )}
                </>
            )}

            {months.map((m) => {
                const stops = stopsOf(m.termMonth, m.reportDate, m.lands);
                const states = statesOf(m.termMonth, stops);
                const monthState: Dot = states.every((s) => s === "done") ? "done" : states.includes("now") ? "now" : "ahead";
                const lines = stops.flatMap((s) => s.lines);
                const isOpen = openMonths.has(m.termMonth);
                const panelId = `plan-month-${m.termMonth}`;
                return (
                    <section key={m.termMonth} className="overflow-hidden rounded-2xl bg-white ring-1 ring-black/[0.04]">
                        <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            onClick={() => setOpenMonths((s) => flip(s, m.termMonth))}
                            // Inset: the month clips its corners, so an outer ring would be cut off.
                            className="flex w-full items-center gap-3 bg-gray-50 p-4 text-left transition-colors hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600"
                        >
                            <PlanDot state={monthState} size={20} />
                            <span className="min-w-0 flex-1">
                                <span className="block text-[15px] font-semibold text-gray-900">
                                    Month {m.termMonth}
                                    {m.termMonth === cur && <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">Now</span>}
                                </span>
                                <span className={cn(TYPE.tc, "text-gray-500")}>
                                    {m.window}
                                    {lines.length > 0 ? ` · ${lines.filter((i) => i.status === "done").length} of ${lines.length} done` : ""}
                                </span>
                            </span>
                            <ChevronDownIcon aria-hidden size={16} className={cn("shrink-0 text-gray-400 transition-transform motion-reduce:transition-none", isOpen && "rotate-180")} />
                        </button>
                        {isOpen && (
                            <ol id={panelId} className="list-plain m-0 border-t border-gray-100 p-4">
                                {stops.map((s, i) => (
                                    <li key={s.key} className="group relative pb-5 pl-8 last:pb-0">
                                        <span aria-hidden className={cn("absolute bottom-0 left-2 top-5 w-0.5 group-last:hidden", states[i] === "done" ? "bg-blue-600" : "bg-gray-200")} />
                                        <span className="absolute left-0 top-0">
                                            <PlanDot state={states[i]} />
                                        </span>
                                        <span className="sr-only">{DOT_WORD[states[i]]}. </span>
                                        <p className={cn(TYPE.tc, "m-0 font-semibold tabular-nums", states[i] === "now" ? "text-amber-700" : "text-gray-500")}>
                                            {s.date ? dayDate(s.date) ?? "Date to be set" : m.window}
                                            {states[i] === "now" && " · You are here"}
                                        </p>
                                        <p className={cn(TYPE.tb, "m-0 font-semibold", states[i] === "ahead" ? "text-gray-600" : "text-gray-900")}>{s.label}</p>
                                        {s.note && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{s.note}</p>}
                                        {s.slip && <p className={cn(TYPE.tc, "m-0 mt-0.5", s.slip.late ? LATE_TEXT : "text-gray-600")}>{s.slip.text}</p>}
                                        {(s.lines.length > 0 || s.lands.length > 0) && (
                                            <ul className="list-plain m-0 mt-2 flex flex-col gap-2 p-0">
                                                {s.lines.map((item) => (
                                                    <PlanLineRow
                                                        key={keyOf(item)}
                                                        item={item}
                                                        engagement={engagement}
                                                        askId={item.status === "waiting_on_you" ? openAskFor(item, asks) : null}
                                                        open={openLines.has(keyOf(item))}
                                                        onToggle={() => setOpenLines((set) => flip(set, keyOf(item)))}
                                                        onAnswer={onAnswer}
                                                    />
                                                ))}
                                                {s.lands.map((text) => (
                                                    <li key={text} className="flex items-start gap-2.5 rounded-xl bg-white p-3 ring-1 ring-inset ring-gray-200">
                                                        <span className="mt-0.5">
                                                            <PlanDot state="ahead" size={14} />
                                                        </span>
                                                        <span className={cn(TYPE.tb, "min-w-0 text-gray-600")}>{text}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </section>
                );
            })}
        </Drawer>
    );
}

/** One piece of work: its name and where it is, and what it says when it is opened, in place. */
function PlanLineRow({
    item,
    engagement,
    askId,
    open,
    onToggle,
    onAnswer,
}: {
    item: ClientPlanItem;
    engagement: ClientPagePayload["engagement"];
    askId: string | null;
    open: boolean;
    onToggle: () => void;
    onAnswer: (askId: string) => void;
}) {
    const panelId = `plan-line-${item.key ?? item.code}`;
    const what = lineDescription(item, engagement);
    const when = lineWhen(item, engagement);
    const measured = measuredSentence(item);
    const dot = lineDot(item);
    return (
        <li className={cn("rounded-xl bg-white p-3 ring-1 ring-inset", open ? "ring-blue-300" : askId ? "ring-amber-200" : "ring-gray-200")}>
            <button type="button" aria-expanded={open} aria-controls={panelId} onClick={onToggle} className={cn("flex w-full items-start gap-2.5 rounded text-left", FOCUS_RING)}>
                <span className="mt-0.5">
                    <PlanDot state={dot} size={14} />
                </span>
                <span className="min-w-0 flex-1">
                    <span className={cn(TYPE.tb, "block font-medium", dot === "ahead" ? "text-gray-600" : "text-gray-900")}>{planLineTitle(item)}</span>
                    <span className={cn(TYPE.tc, askId ? "font-medium text-amber-700" : "text-gray-500")}>{askId ? "Needs your answer" : item.note ?? DOT_WORD[dot]}</span>
                </span>
                <ChevronDownIcon aria-hidden size={16} className={cn("mt-0.5 shrink-0 text-gray-400 transition-transform motion-reduce:transition-none", open && "rotate-180")} />
            </button>
            {open && (
                <div id={panelId} className={cn(TYPE.tb, "mt-2 flex flex-col gap-2 rounded-xl bg-gray-50 p-3 leading-6 text-gray-900")}>
                    {item.line && <Part label="Where it stands">{item.line}</Part>}
                    {what && <Part label="What it is">{what}</Part>}
                    {item.fixed && item.fixed.length > 0 && (
                        <Part label="What we fixed">
                            <ul className="list-plain m-0 p-0">
                                {item.fixed.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <CheckIcon aria-hidden size={14} className="mt-1 shrink-0 text-blue-600" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </Part>
                    )}
                    {when && <Part label="When">{when}</Part>}
                    {measured && <Part label="What we check">{measured}</Part>}
                    {!item.line && !what && !when && !measured && <p className="m-0 text-gray-500">Your account manager adds the detail as the work starts.</p>}
                    {askId && (
                        <div className="flex justify-end">
                            <PillButton variant="primary" size="sm" onClick={() => onAnswer(askId)}>
                                Answer now
                            </PillButton>
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}

function Part({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0")}>{label}</p>
            {children}
        </div>
    );
}
